import { UserData } from '../types';

const MONTHLY_PRICES: Record<string, { month: number; annual: number }> = {
  essential:    { month: 50, annual: 42 },
  professional: { month: 90, annual: 75 },
  starter:      { month: 5,  annual: 5 },
  scholar:      { month: 15, annual: 15 },
};

function toDateStr(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function computeMRR(users: UserData[]): number {
  return users.reduce((sum, u) => {
    if (!u.subscribed || !u.plan || u.affiliate) return sum;
    const prices = MONTHLY_PRICES[u.plan];
    if (!prices) return sum;
    return sum + (u.billingInterval === 'year' ? prices.annual : prices.month);
  }, 0);
}

export function computeActiveUsers(users: UserData[], days: number): number {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  const cutoffStr = toDateStr(cutoff);
  return users.filter(u => {
    if (!u.dailyActivity) return false;
    return Object.keys(u.dailyActivity).some(d => d >= cutoffStr);
  }).length;
}

function countActiveSince(users: UserData[], refDate: Date, lookback: number): number {
  const start = new Date(refDate);
  start.setDate(start.getDate() - lookback + 1);
  const startStr = toDateStr(start);
  const endStr = toDateStr(refDate);
  return users.filter(u => {
    if (!u.dailyActivity) return false;
    return Object.keys(u.dailyActivity).some(d => d >= startStr && d <= endStr);
  }).length;
}

export function computeDAUHistory(users: UserData[], days: number): Array<{ date: string; dau: number; wau: number; mau: number }> {
  const result: Array<{ date: string; dau: number; wau: number; mau: number }> = [];
  const today = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const dateStr = toDateStr(d);
    const dau = users.filter(u => u.dailyActivity?.[dateStr]).length;
    const wau = countActiveSince(users, d, 7);
    const mau = countActiveSince(users, d, 30);
    result.push({ date: dateStr, dau, wau, mau });
  }
  return result;
}

export function computeConversionRate(users: UserData[]): number {
  const nonAffiliate = users.filter(u => !u.affiliate);
  if (nonAffiliate.length === 0) return 0;
  return (nonAffiliate.filter(u => u.subscribed).length / nonAffiliate.length) * 100;
}

export function computeChurn(users: UserData[]): number {
  return users.filter(u => u.subscriptionStart && !u.subscribed && !u.affiliate).length;
}

export function computeFeatureUsage(users: UserData[], days: number): Array<{ date: string; chat: number; gist: number; discover: number; citationClick: number; timePanel: number }> {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  const cutoffStr = toDateStr(cutoff);
  const totals: Record<string, { chat: number; gist: number; discover: number; citationClick: number; timePanel: number }> = {};

  users.forEach(u => {
    if (!u.dailyActivity) return;
    for (const [date, bucket] of Object.entries(u.dailyActivity)) {
      if (date < cutoffStr) continue;
      if (!totals[date]) totals[date] = { chat: 0, gist: 0, discover: 0, citationClick: 0, timePanel: 0 };
      const t = totals[date];
      const b = bucket as Record<string, number>;
      t.chat += (bucket.m || 0) + (b.cm || 0) + (b.sm || 0);
      t.discover += (bucket.d || 0) + (b.cd || 0) + (b.sd || 0);
      t.gist += (bucket.g || 0) + (b.cg || 0) + (b.sg || 0);
      t.citationClick += bucket.cc || 0;
      t.timePanel += bucket.tp || 0;
    }
  });

  // Fill in missing days
  const today = new Date();
  const result: Array<{ date: string; chat: number; gist: number; discover: number; citationClick: number; timePanel: number }> = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const dateStr = toDateStr(d);
    result.push({ date: dateStr, ...(totals[dateStr] || { chat: 0, gist: 0, discover: 0, citationClick: 0, timePanel: 0 }) });
  }
  return result;
}

export function computeEngagementBuckets(users: UserData[]): Array<{ bucket: string; count: number }> {
  const buckets: Record<string, number> = { '0': 0, '1-5': 0, '6-20': 0, '21-50': 0, '50+': 0 };
  users.forEach(u => {
    const m = u.messageCount || 0;
    if (m === 0) buckets['0']++;
    else if (m <= 5) buckets['1-5']++;
    else if (m <= 20) buckets['6-20']++;
    else if (m <= 50) buckets['21-50']++;
    else buckets['50+']++;
  });
  return Object.entries(buckets).map(([bucket, count]) => ({ bucket, count }));
}

export function computeRegionalBreakdown(users: UserData[]): { ca: { users: number; messages: number }; us: { users: number; messages: number } } {
  let caUsers = 0, caMessages = 0, usUsers = 0, usMessages = 0;
  users.forEach(u => {
    if ((u.canliiMessages || 0) > 0) { caUsers++; caMessages += u.canliiMessages || 0; }
    if ((u.scholarMessages || 0) > 0) { usUsers++; usMessages += u.scholarMessages || 0; }
  });
  return { ca: { users: caUsers, messages: caMessages }, us: { users: usUsers, messages: usMessages } };
}

export function computeFunnel(users: UserData[], funnelStats?: { installs: number; opens: number }): Array<{ stage: string; count: number; pct: number }> {
  const signedUp = users.length;
  // Users with messages are assumed onboarded (pre-onboarding users didn't have the flow)
  const onboarded = users.filter(u => u.onboardingComplete || (u.messageCount || 0) > 0).length;
  const firstMessage = users.filter(u => (u.messageCount || 0) > 0).length;
  const subscribed = users.filter(u => u.subscribed).length;

  const stages: Array<{ stage: string; count: number }> = [];

  // Installed and Opened must be >= signedUp for a valid funnel
  if (funnelStats) {
    const installs = Math.max(funnelStats.installs, signedUp);
    const opens = Math.max(funnelStats.opens, signedUp);
    stages.push({ stage: 'Installed', count: installs });
    stages.push({ stage: 'Opened', count: opens });
  }

  stages.push({ stage: 'Signed Up', count: signedUp });
  stages.push({ stage: 'Onboarded', count: onboarded });
  stages.push({ stage: 'First Message', count: firstMessage });
  stages.push({ stage: 'Subscribed', count: subscribed });

  const top = stages[0]?.count || 1;
  return stages.map(s => ({ ...s, pct: top > 0 ? Math.round((s.count / top) * 100) : 0 }));
}

export function computeAvgMessagesPerUser(users: UserData[]): number {
  const active = users.filter(u => (u.messageCount || 0) > 0);
  if (active.length === 0) return 0;
  return active.reduce((sum, u) => sum + (u.messageCount || 0), 0) / active.length;
}

export function computeOnboardingRate(users: UserData[]): number {
  if (users.length === 0) return 0;
  return (users.filter(u => u.onboardingComplete).length / users.length) * 100;
}

export function computeARPU(users: UserData[]): number {
  const subscribed = users.filter(u => u.subscribed && !u.affiliate);
  if (subscribed.length === 0) return 0;
  return computeMRR(users) / subscribed.length;
}

export function formatCurrency(value: number): string {
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export function formatPct(value: number): string {
  return `${value.toFixed(1)}%`;
}

export function formatDateLabel(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
